import type { FC, ReactNode } from 'react';

export interface WidgetCardProps {
  title: string;
  subtitle?: string;
  /** First load — renders a placeholder block of `placeholderHeight` instead of children. */
  loading?: boolean;
  /** Rendered instead of children when there is genuinely nothing to show. */
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  isEmpty?: boolean;
  /** Keeps the card's geometry stable between the loading and loaded states. */
  placeholderHeight?: number;
  headerAccessory?: ReactNode;
  className?: string;
  children?: ReactNode;
}

/**
 * Shared frame for every dashboard widget, so loading / empty / loaded states
 * look identical and the grid never reflows as data lands.
 */
export const WidgetCard: FC<WidgetCardProps> = ({
  title,
  subtitle,
  loading = false,
  emptyMessage,
  emptyIcon,
  isEmpty = false,
  placeholderHeight = 200,
  headerAccessory,
  className,
  children,
}) => {
  const body = (() => {
    if (loading) {
      return (
        <div
          className="dashboard-card__placeholder"
          style={{ height: placeholderHeight }}
          aria-hidden="true"
        />
      );
    }

    if (isEmpty) {
      // Deliberately NOT placeholderHeight: that reserves the space the populated
      // state needs, which would leave a tall empty card (and stretch its
      // neighbours in the same row) just to show one line of text.
      return (
        <div className="dashboard-card__empty">
          {emptyIcon && <div className="dashboard-card__empty-icon">{emptyIcon}</div>}
          <p className="dashboard-card__empty-text">{emptyMessage}</p>
        </div>
      );
    }

    return children;
  })();

  return (
    <section className={['dashboard-card', className].filter(Boolean).join(' ')}>
      <header className="dashboard-card__header">
        <div>
          <h2 className="dashboard-card__title">{title}</h2>
          {subtitle && <p className="dashboard-card__subtitle">{subtitle}</p>}
        </div>
        {headerAccessory}
      </header>
      <div className="dashboard-card__body">{body}</div>
    </section>
  );
};
